import { css } from 'aphrodite';
import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { PhoneIcon } from '../../../web/components/svgs/icons/PhoneIcon';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { TargetGoalsIcon } from '../../../web/components/svgs/icons/TargetGoalsIcon';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerProvider,
	CoffeeDrawerToolbar,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { CallDetailsDrawerContent } from '../../components/CallRecordingDetails/CallDetailsDrawerContent';
import { LeadView } from '../../components/leadView/LeadView';
import { PostCallFeedbackPanel } from '../../components/PostCallFeedbackPanel';
import { RepCoachingGoalsContent } from '../../components/RepCoachingGoalsContent';
import { Tabs } from '../../components/Tabs';
import { ViewportContext } from '../../hooks/useViewport';
import { brandSecondary, white } from '../../styles/colors';
import { CallLogsTab } from './components/CallLogsTab';
import { IncomingCallsTab } from './components/IncomingCallsTab';
import { styleSheet } from './styles';

export const COFFEE_DRAWER_VALUE_SDR_GOALS = 'coffee-sdr-goals';
export const COFFEE_DRAWER_VALUE_AI_FEEDBACK = 'coffee-ai-feedback';
export const COFFEE_DRAWER_VALUE_CALL_DETAILS = 'coffee-call-details';

export const CallLogContainer: React.FC = () => {
	const { isBelowSmall } = useContext(ViewportContext);
	const history = useHistory();
	const [selectedDrawerValue, setSelectedDrawerValue] = useState<string | null>(null);
	const [sdrGoalsState, setSdrGoalsState] = useState<{ userId: string; userName: string } | null>(null);
	const [aiFeedbackState, setAiFeedbackState] = useState<{ phoneCallId: string; transcriptionId: string } | null>(null);
	const [callDetailsState, setCallDetailsState] = useState<{ phoneCallId: string; autoplay?: boolean } | null>(null);

	const drawerValue = sdrGoalsState || aiFeedbackState || callDetailsState ? selectedDrawerValue : null;

	const handleDrawerValueChange = useCallback(
		(newValue: string) => {
			setSelectedDrawerValue(newValue);

			// When drawer closes (empty value) and we had call details open, navigate back
			if (!newValue && callDetailsState) {
				setCallDetailsState(null);
				history.push('/call-log');
			}
		},
		[callDetailsState, history]
	);

	const handleOpenSdrGoals = (userId: string, userName: string) => {
		setSdrGoalsState({ userId, userName });
		setSelectedDrawerValue(COFFEE_DRAWER_VALUE_SDR_GOALS);
	};

	const handleCloseSdrGoals = () => {
		setSdrGoalsState(null);
		setSelectedDrawerValue(null);
	};

	const handleOpenAIFeedback = (phoneCallId: string, transcriptionId: string) => {
		setAiFeedbackState({ phoneCallId, transcriptionId });
		setSelectedDrawerValue(COFFEE_DRAWER_VALUE_AI_FEEDBACK);
	};

	const handleCloseAIFeedback = () => {
		setAiFeedbackState(null);
		setSelectedDrawerValue(null);
	};

	const handleOpenCallDetails = (phoneCallId: string, autoplay?: boolean) => {
		setCallDetailsState({ phoneCallId, autoplay });
		setSelectedDrawerValue(COFFEE_DRAWER_VALUE_CALL_DETAILS);
	};

	return (
		<CoffeeDrawerProvider value={drawerValue} onValueChange={handleDrawerValueChange}>
			<div className={css(styleSheet.root)}>
				<div className={css(styleSheet.callLogRoot)}>
					<Tabs
						defaultTab='call-log'
						tabs={[
							{
								content: (
									<div className={css(styleSheet.viewContainer)}>
										<CallLogsTab
											onOpenSdrGoals={handleOpenSdrGoals}
											onOpenAIFeedback={handleOpenAIFeedback}
											onOpenCallDetails={handleOpenCallDetails}
										/>
									</div>
								),
								name: 'call-log',
								renderLabel: selectedTab => (
									<LeadView.Title
										color={selectedTab !== 'call-log' ? brandSecondary : white}
										size='small'
										title={isBelowSmall ? 'Call Log' : 'Call Logs'}
									/>
								),
							},
							{
								content: (
									<div className={css(styleSheet.viewContainer)}>
										<IncomingCallsTab />
									</div>
								),
								name: 'incoming',
								renderLabel: selectedTab => (
									<LeadView.Title
										color={selectedTab !== 'incoming' ? brandSecondary : white}
										size='small'
										title={isBelowSmall ? 'Incoming' : 'Incoming Calls'}
									/>
								),
							},
						]}
					/>
				</div>
				<SideDrawer styles={[styleSheet.drawer]}>
					<AppDrawerContent value={COFFEE_DRAWER_VALUE_SDR_GOALS} styles={[styleSheet.drawerContent]}>
						{sdrGoalsState ? (
							<RepCoachingGoalsContent
								userId={sdrGoalsState.userId}
								userName={sdrGoalsState.userName}
								onClose={handleCloseSdrGoals}
							/>
						) : null}
					</AppDrawerContent>
					<AppDrawerContent value={COFFEE_DRAWER_VALUE_AI_FEEDBACK} styles={[styleSheet.drawerContent]}>
						{aiFeedbackState ? (
							<PostCallFeedbackPanel
								phoneCallId={aiFeedbackState.phoneCallId}
								transcriptionId={aiFeedbackState.transcriptionId}
								onClose={handleCloseAIFeedback}
							/>
						) : null}
					</AppDrawerContent>
					<AppDrawerContent value={COFFEE_DRAWER_VALUE_CALL_DETAILS} styles={[styleSheet.drawerContent]}>
						{callDetailsState ? (
							<CallDetailsDrawerContent
								autoplay={callDetailsState.autoplay}
								onOpenAIFeedback={handleOpenAIFeedback}
								recentCallId={callDetailsState.phoneCallId}
							/>
						) : null}
					</AppDrawerContent>
					<CoffeeDrawerToolbar>
						<AppDrawerToolbarButton
							value={COFFEE_DRAWER_VALUE_CALL_DETAILS}
							tooltip='Call Details'
							disabled={!callDetailsState}
						>
							<PhoneIcon />
						</AppDrawerToolbarButton>
						<AppDrawerToolbarButton
							value={COFFEE_DRAWER_VALUE_SDR_GOALS}
							tooltip='SDR coaching goals'
							disabled={!sdrGoalsState}
						>
							<TargetGoalsIcon />
						</AppDrawerToolbarButton>
						<AppDrawerToolbarButton
							value={COFFEE_DRAWER_VALUE_AI_FEEDBACK}
							tooltip='AI Call Feedback'
							disabled={!aiFeedbackState}
						>
							<SparklesIcon />
						</AppDrawerToolbarButton>
					</CoffeeDrawerToolbar>
				</SideDrawer>
			</div>
		</CoffeeDrawerProvider>
	);
};
